import React, { useEffect, useMemo, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Actions
import { getUserData } from '~/redux/actions/auth-actions';
import { failure } from '~/redux/actions/snackbar-actions';
import { getAllProducts, getAllTemplates } from '~/redux/actions/template-builder';
import {
  buyDesignerCredits,
  createDesignerRequest,
  fetchDesignerCreditPackages,
  fetchDesignerHistory,
} from '~/redux/actions/custom-template-query-actions';

// Utils
import { dateFormat } from '~/utils/date-format';
import { getAvailableDesignerCredits } from '~/utils/design-credits';
import {
  DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE,
  DESIGNER_QUERY_PROOF_PREVIEW_STATUSES,
} from '~/utils/constants';

// MUI Components
import { Container, Box, Typography, Button, CircularProgress } from '@mui/material';

// Components
import ConfirmModal from '~/components/General/ConfirmModal';
import HistoryListing from '~/components/Templates/HireDesigner/HistoryListing';
import HireForm from '~/components/Templates/HireDesigner/HireForm';
import PaymentMethod from '~/components/Account/Wallet/PaymentMethod';
import Stripe from '~/components/Stripe/Stripe';
import OlcButton from '~/components/General/OlcButton';

// Icons
import {
  Step1CIcon,
  Step2CIcon,
  ArrowHireCIcon,
  ContactInfoCIcon,
  ConfirmPurchaseCIcon,
} from '~/assets/images';

// Styles
import './styles.scss';

const isDesignerQueryProofPreviewable = (status = '') =>
  DESIGNER_QUERY_PROOF_PREVIEW_STATUSES.includes(status);

const formatUsd = (value, decimals = 0) =>
  Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const getCreditTitle = (credits) => `${credits} ${credits === 1 ? 'Credit' : 'Credits'}`;
const getCreditPackageTitle = (credits) => `${credits} Credit Package`;

const mapProductOptions = (products = []) =>
  products
    .map((product) => {
      if (product.productType === 'Postcards') {
        return product.size.map((size) => ({
          id: size.id,
          title: `${product.productType} - ${size.size}`,
        }));
      }

      if (product.productType === 'Professional Letters') {
        const windowedLabel = product.windowed ? 'Windowed' : 'Non-Windowed';
        return {
          id: product.id,
          title: `${product.productType} - ${windowedLabel}`,
        };
      }

      return {
        id: product.id,
        title: product.productType,
      };
    })
    .flat();

const mapHistoryRow = (row) => ({
  id: row.id,
  templateName: row?.meta?.templateTitle || 'Untitled Template',
  templateId: row.id,
  date: dateFormat(row.createdAt, true) || '-',
  productType:
    [row?.product?.productType, row?.product?.paperSize].filter(Boolean).join(' ') || '-',
  submittedDate: dateFormat(row.createdAt) || '-',
  submittedBy: row?.creator?.fullName || '-',
  status: row.status || '-',
  completedDate: row.completedAt ? dateFormat(row.completedAt) : '-',
  raw: row,
});

const HireDesigner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.userReducers.user);
  const products = useSelector((state) => state.templateReducer.products);
  const templates = useSelector((state) => state.templateReducer.templates.rows) || [];
  const templatesPagination = useSelector((state) => state.templateReducer.templatesPagination);
  const creditPackages = useSelector((state) => state.designerQueriesReducers.creditPackages);
  const packagesLoading = useSelector((state) => state.designerQueriesReducers.packagesLoading);
  const history = useSelector((state) => state.designerQueriesReducers.history);
  const historyLoading = useSelector((state) => state.designerQueriesReducers.historyLoading);

  const availableCredits = useMemo(() => getAvailableDesignerCredits(userData), [userData]);
  const productOptions = useMemo(() => mapProductOptions(products), [products]);
  const templateOptions = useMemo(
    () =>
      (templates || [])
        .filter((template) => template?.templateType === 'json')
        .map((template) => ({
          id: template.id,
          title: `ID:${template.id} ${template.title}`,
          object: template,
        })),
    [templates]
  );
  const historyRows = useMemo(() => (history?.rows || []).map(mapHistoryRow), [history?.rows]);
  const userEmail = userData?.email || '';

  const [openHireForm, setOpenHireForm] = useState(false);
  const [paymentMethodModal, setPaymentMethodModal] = useState(false);
  const [selectedCreditPackage, setSelectedCreditPackage] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [submittingDesign, setSubmittingDesign] = useState(false);
  const [historyPerPage, setHistoryPerPage] = useState(5);
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  useEffect(() => {
    dispatch(getUserData());
    dispatch(fetchDesignerCreditPackages());
    dispatch(fetchDesignerHistory({ page: 1, perPage: 5 }));
  }, [dispatch]);

  useEffect(() => {
    if (products.length === 0) {
      dispatch(getAllProducts());
    }
  }, [dispatch, products.length]);

  useEffect(() => {
    if (openHireForm) {
      dispatch(
        getAllTemplates(
          DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.page,
          DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.pageSize,
          ''
        )
      );
    }
  }, [dispatch, openHireForm]);

  useEffect(() => {
    if (!selectedCreditPackage && creditPackages.length) {
      setSelectedCreditPackage(
        creditPackages.find((credit) => credit.popular) || creditPackages[0]
      );
    }
  }, [creditPackages, selectedCreditPackage]);

  const closeConfirmModal = () => {
    setOpenConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
  };

  const updateConfirmModalLoading = (loading) => {
    setOpenConfirmModal((prev) => ({ ...prev, loading }));
  };

  const handleOpenPaymentMethodModal = () => {
    closeConfirmModal();
    setPaymentMethodModal(true);
  };

  const handlePurchaseCredits = async (creditPackage, canPurchasePackage) => {
    if (!canPurchasePackage) {
      dispatch(failure('Please add a payment method to purchase this design credit package.'));
      return;
    }

    updateConfirmModalLoading(true);
    const response = await dispatch(buyDesignerCredits(creditPackage.id));
    const didPurchase = response?.status === 200 && response?.data?.data?.success;

    if (didPurchase) {
      closeConfirmModal();
      return;
    }

    updateConfirmModalLoading(false);
  };

  const handleOpenConfirmPurchase = () => {
    const creditPackage = selectedCreditPackage;

    if (!creditPackage) {
      dispatch(failure('Please select a design credit package.'));
      return;
    }

    const walletBalance = Number(userData?.organization?.balance || 0);
    const hasPaymentMethod = Boolean(userData?.organization?.defaultPaymentMethodStripeId);
    const walletCanCoverPackage = walletBalance >= Number(creditPackage.totalCost || 0);
    const canPurchasePackage = walletCanCoverPackage || hasPaymentMethod;

    setOpenConfirmModal({
      type: 'export',
      open: true,
      message: (
        <>
          You selected the{' '}
          <strong style={{ color: '#000' }}>
            {getCreditPackageTitle(creditPackage.credits)} for {formatUsd(creditPackage.totalCost)}
          </strong>
          . We&apos;ll deduct the cost from your Wallet balance. If your balance is insufficient,
          the remaining amount will be charged to your saved payment method.
        </>
      ),
      handleOk: () => handlePurchaseCredits(creditPackage, canPurchasePackage),
      successButtonName: 'Purchase',
      successButtonDisabled: !canPurchasePackage,
      loading: false,
      header: 'Confirm Purchase',
      icon: ConfirmPurchaseCIcon,
      className: 'purchaseConfirmModal hireDesignerPurchaseConfirmModal',
      modalName: 'purchaseConfirmModal',
      addOnNote: !canPurchasePackage ? (
        <Typography component="div">
          You have no payment method{' '}
          <Button className="addPaymentMethodBtn" onClick={handleOpenPaymentMethodModal}>
            Add Payment Method
          </Button>
        </Typography>
      ) : null,
      handleClose: closeConfirmModal,
    });
  };

  const handleOpenHireForm = () => {
    if (availableCredits < 1) {
      dispatch(failure('Design credits are required to start a new design.'));
      return;
    }

    setOpenHireForm(true);
  };

  const handleCloseHireForm = () => {
    setOpenHireForm(false);
    setSelectedTemplate(null);
  };

  const searchTemplates = (value) =>
    dispatch(
      getAllTemplates(
        DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.page,
        DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.pageSize,
        value || ''
      )
    );

  const handleSubmitDesign = async (values) => {
    if (availableCredits < 1) {
      dispatch(failure('Design credits are required to start a new design.'));
      return false;
    }

    setSubmittingDesign(true);

    const formData = new FormData();
    formData.append('title', values.templateName);
    formData.append('productId', values.productId);
    formData.append('email', values.email);
    formData.append('comments', values.comments);

    if (values.templateId) {
      formData.append('templateId', values.templateId);
      formData.append('sourceTemplateId', values.templateId);
    }

    if (values.url?.trim()) {
      formData.append('url', values.url.trim());
    } else if (values.videoUrl?.trim()) {
      formData.append('url', values.videoUrl.trim());
    }

    values.files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await dispatch(createDesignerRequest(formData, 1, historyPerPage));
    const didCreate = response?.status === 200;

    setSubmittingDesign(false);

    if (didCreate) {
      setSelectedTemplate(null);
      setOpenHireForm(false);
      return true;
    }

    return false;
  };

  const handleReviewProof = (row) => {
    if (!isDesignerQueryProofPreviewable(row?.status)) {
      return;
    }

    navigate(`/hire-a-designer/proof/${row.id}`);
  };

  return (
    <Container maxWidth="xxl" className="hireDesignerPage">
      <Box className="hireDesignerContainer">
        <Box className="hireDesignerWrapper">
          <Box>
            <Typography variant="h4">Hire a Designer</Typography>
            <Typography variant="body1" className="hireDesignerSubtitle">
              Work with our in-house designers to create custom templates that fit your brand.
            </Typography>
          </Box>
          <Box className="howItWorksWrapper">
            <Typography className="howItWorksTitle">
              <span>
                <img src={ContactInfoCIcon} alt="how it works" />
              </span>
              How it Works
            </Typography>
            <Box className="howItWorksStepsWrapper">
              <Box className="howItWorksStep">
                <img src={Step1CIcon} alt="step 1" />
                <Typography>1. Submit your design request</Typography>
              </Box>
              <Box className="howItWorksStep">
                <img src={ArrowHireCIcon} className="arrowHireIcon" alt="arrow hire" />
              </Box>
              <Box className="howItWorksStep">
                <img src={Step2CIcon} alt="step 2" />
                <Typography>2. Receive your design within 24 business hours</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className="creditsWrapper">
          <Box>
            <Typography variant="h4">Available Credits</Typography>
            <Typography variant="body1">Credits reset on your billing cycle.</Typography>
          </Box>
          <Typography className="creditsCount">{availableCredits}</Typography>
          <OlcButton
            variant="primary"
            className="startNewDesignButton"
            fullWidth
            onClick={handleOpenHireForm}
            disabled={availableCredits < 1}
          >
            Request Design Help
          </OlcButton>
        </Box>
        <Box className="purchaseCreditsWrapper">
          <Typography variant="h4">Purchase Design Credits</Typography>
          {packagesLoading ? (
            <Box className="packagesLoading">
              <CircularProgress
                sx={{
                  color: '#ed5c2f',
                  width: '28px !important',
                  height: '28px !important',
                }}
              />
            </Box>
          ) : (
            <Box className="creditPackagesList">
              {creditPackages.map((credit) => (
                <Box
                  className={`creditDetailsWrapper ${credit.popular ? 'popular' : ''} ${
                    selectedCreditPackage?.id === credit.id ? 'selected' : ''
                  }`}
                  key={credit.id}
                  onClick={() => setSelectedCreditPackage(credit)}
                >
                  <Typography>
                    {getCreditTitle(credit.credits)}{' '}
                    <span>{formatUsd(credit.pricePerCredit, 2)} per credit</span>
                  </Typography>
                  <Typography>{formatUsd(credit.totalCost)}</Typography>
                </Box>
              ))}
            </Box>
          )}
          <OlcButton
            variant="primary"
            className="purchaseCreditsButton"
            fullWidth
            onClick={handleOpenConfirmPurchase}
            disabled={!selectedCreditPackage || packagesLoading}
          >
            Purchase Credits
          </OlcButton>
        </Box>
      </Box>

      <HireForm
        openHireForm={openHireForm}
        handleCloseHireForm={handleCloseHireForm}
        onSubmit={handleSubmitDesign}
        defaultEmail={userEmail}
        loading={submittingDesign}
        productOptions={productOptions}
        templateOptions={templateOptions}
        selectedTemplate={selectedTemplate}
        onSelectedTemplateChange={setSelectedTemplate}
        onTemplateSearch={searchTemplates}
        templatesLoading={templatesPagination?.loading}
      />

      <HistoryListing
        rows={historyRows}
        loading={historyLoading}
        pagination={history}
        onPageChange={(page) => dispatch(fetchDesignerHistory({ page, perPage: historyPerPage }))}
        onPerPageChange={(perPage) => {
          setHistoryPerPage(perPage);
          dispatch(fetchDesignerHistory({ page: 1, perPage }));
        }}
        onReviewProof={handleReviewProof}
      />

      <ConfirmModal openConfirmModal={openConfirmModal} />

      <Stripe>
        <PaymentMethod
          open={paymentMethodModal}
          handleClose={() => {
            setPaymentMethodModal(false);
          }}
          onSuccessMethod={() => {
            dispatch(getUserData());
          }}
        />
      </Stripe>
    </Container>
  );
};

export default HireDesigner;
